import { motion } from "framer-motion";
import { Building2, Users, Shield, FileCheck, CheckCircle2, ArrowRight, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const pillars = [
  {
    icon: Briefcase,
    sectionTitle: "Centralisez vos documents d'entreprise",
    sectionSubtitle: "KBIS, attestations URSSAF, RC Pro, décennale... Tous vos documents légaux à jour et accessibles en un clic.",
    title: "Mes documents légaux",
    description: "Plus jamais d'assurance expirée ou de document oublié. Alertes automatiques avant chaque échéance.",
    items: ["KBIS à jour", "Assurance RC Pro", "Assurance décennale", "Attestation URSSAF", "Carte professionnelle"],
    color: "from-primary to-primary/80",
    bgColor: "bg-primary/10",
    cta: { label: "Gérer mes documents", link: "/legal-documents" },
  },
  {
    icon: Users,
    sectionTitle: "Gérez la conformité de vos sous-traitants",
    sectionSubtitle: "Centralisez, vérifiez et suivez les documents de conformité de vos prestataires en temps réel.",
    title: "Suivi sous-traitants",
    description: "Score de conformité, alertes avant expiration et portail dédié pour vos prestataires.",
    items: ["Documents centralisés", "Score de conformité", "Alertes automatiques", "Portail sous-traitant", "Export rapide"],
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-500/10",
    cta: { label: "Gérer mes sous-traitants", link: "/subcontractors" },
  },
];

export const B2BSection = () => {
  return (
    <section className="relative py-20 md:py-28 px-6 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
      
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Pour les professionnels</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4">
            Conformité simplifiée,{" "}
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              zéro stress
            </span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Gérez vos documents légaux et ceux de vos sous-traitants depuis une seule plateforme.
          </p>
        </motion.div>

        {/* Two Pillars */}
        <div className="space-y-16 md:space-y-20 mb-12">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-6"
            >
              {/* Section Header */}
              <div className={`text-center ${index === 1 ? 'md:text-left' : 'md:text-right'}`}>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">
                  {pillar.sectionTitle}
                </h3>
                <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto md:mx-0 md:ml-auto">
                  {pillar.sectionSubtitle}
                </p>
              </div>

              {/* Card */}
              <div className={`relative p-6 md:p-8 rounded-2xl bg-background/80 backdrop-blur-sm border border-border/60 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 ${index === 1 ? 'md:ml-auto md:mr-0' : 'md:mr-auto md:ml-0'} md:max-w-lg`}>
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-5 shadow-lg`}>
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Content */}
                <h4 className="text-xl md:text-2xl font-bold mb-3">{pillar.title}</h4>
                <p className="text-muted-foreground mb-6">{pillar.description}</p>
                
                {/* Checklist */}
                <div className="space-y-2.5 mb-6">
                  {pillar.items.map((item, i) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <Link to={pillar.cta.link}>
                  <Button variant="outline" size="sm" className="group">
                    {pillar.cta.label}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-8 md:gap-16 py-8 px-6 rounded-2xl bg-muted/50 border border-border/30 mb-10"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-primary">2 000+</div>
            <div className="text-sm text-muted-foreground">Entreprises</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-emerald-500">98%</div>
            <div className="text-sm text-muted-foreground">Taux de conformité</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-foreground">5h</div>
            <div className="text-sm text-muted-foreground">Gagnées / mois</div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Link to="/auth?mode=signup">
            <Button size="lg" variant="glow" className="w-full sm:w-auto group">
              Essayer gratuitement
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-4">
            <Shield className="w-4 h-4 inline mr-1" />
            Sans engagement · Données hébergées en France
          </p>
        </motion.div>
      </div>
    </section>
  );
};
